// client/src/pages/SmsConsentPage.js — the public text-alerts opt-in page (/sms-consent)
// that 10DLC campaign registration points to. The wording comes from the server
// (lib/smsConsent) so it carries this site's brokerage name; the box starts unticked.
import React, { useEffect, useState } from 'react';

const SmsConsentPage = () => {
    const [info, setInfo] = useState(null);
    const [form, setForm] = useState({ name: '', phone: '', email: '', note: '' });
    const [agreed, setAgreed] = useState(false);
    const [status, setStatus] = useState('idle');
    const [error, setError] = useState('');

    useEffect(() => {
        fetch('/api/public/desk/sms-consent').then((r) => r.json()).then(setInfo).catch(() => setError('The page could not load. Please try again.'));
    }, []);

    const change = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
    const submit = async (e) => {
        e.preventDefault();
        setStatus('submitting'); setError('');
        try {
            const r = await fetch('/api/public/desk/sms-consent', {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...form, agreed, wording_shown: info && info.wording }),
            });
            const body = await r.json();
            if (!r.ok) throw new Error(body.message || 'Something went wrong.');
            setStatus('done');
        } catch (err) { setError(err.message); setStatus('idle'); }
    };

    const brand = (info && info.brand) || 'Business Real Estate Corp.';
    const input = 'w-full border border-gray-300 rounded-md px-3 py-3 text-base';
    return (
        <div className="container mx-auto px-4 py-10">
            <div className="max-w-2xl mx-auto">
                <h1 className="text-3xl font-bold mb-2">{brand} text alerts</h1>
                <p className="text-gray-600 mb-6">Get showing confirmations, maintenance scheduling, payment receipts and lease notices by text message.</p>
                {status === 'done' ? (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-5">
                        <p className="font-semibold text-green-800">You are signed up for {info && info.program}.</p>
                        <p className="text-green-800 mt-2">Reply STOP to any message to cancel, or HELP for help.</p>
                    </div>
                ) : (
                    <form onSubmit={submit} className="bg-white border rounded-lg p-5 space-y-4">
                        <label className="block"><span className="font-semibold">Your name</span>
                            <input name="name" value={form.name} onChange={change} className={input} required autoComplete="name" /></label>
                        <label className="block"><span className="font-semibold">Mobile number</span>
                            <input name="phone" type="tel" value={form.phone} onChange={change} className={input} required autoComplete="tel" /></label>
                        <label className="block"><span className="font-semibold">E-mail (optional)</span>
                            <input name="email" type="email" value={form.email} onChange={change} className={input} autoComplete="email" /></label>
                        <label className="block"><span className="font-semibold">Anything you want to tell us (optional)</span>
                            <textarea name="note" value={form.note} onChange={change} className={input} rows={3} /></label>
                        <div className="flex gap-3 items-start bg-gray-50 border rounded-md p-4">
                            <input id="sms-agree" type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} className="mt-1 h-6 w-6 flex-none" />
                            <label htmlFor="sms-agree" className="text-sm">{info ? info.wording : 'Loading…'}</label>
                        </div>
                        {error && <p className="text-red-700 font-semibold">{error}</p>}
                        <button type="submit" disabled={!agreed || !info || status === 'submitting'}
                            className="w-full bg-blue-700 text-white font-semibold rounded-md py-3 disabled:opacity-50">
                            {status === 'submitting' ? 'Saving…' : 'Sign up for text alerts'}
                        </button>
                    </form>
                )}
                <div className="mt-8 text-sm text-gray-600 space-y-2">
                    <h2 className="text-lg font-semibold text-gray-800">About this program</h2>
                    <ul className="list-disc pl-5 space-y-1">
                        <li>Program: {info ? info.program : `${brand} text alerts`}. Messages are about your own showings, maintenance requests, payments, leases and questions.</li>
                        <li>Message frequency varies. Message and data rates may apply.</li>
                        <li>Text STOP to cancel at any time; text HELP for help, or call our office.</li>
                        <li>Consent is not a condition of any purchase, lease or service. We do not sell or share mobile numbers or opt-in consent with third parties for their marketing.</li>
                        <li>Carriers are not liable for delayed or undelivered messages.</li>
                    </ul>
                    {info && info.address && <p>{brand}, {info.address}</p>}
                </div>
            </div>
        </div>
    );
};

export default SmsConsentPage;
